import * as React from 'react';
import styles from './Curriculum.module.css';

// =========================================
// Data Definitions
// =========================================

const education = [
  {
    period: '2019-2023',
    school: {
      en: 'University of Florence',
      it: 'Università di Firenze'
    },
    degree: {
      en: 'Bachelor’s Degree in Architectural Sciences',
      it: 'Laurea triennale in Scienze dell’Architettura'
    },
    thesis: {
      en: 'Thesis: "In the footsteps of memory". Reuse of secondary electrical cabins along the Via Francigena.',
      it: 'Tesi: "Sulle tracce del ricordo". Percorso di riuso delle cabine elettriche secondarie lungo la Via Francigena.'
    },
    graduation: {
      en: 'Graduation date: 11/07/2023',
      it: 'Data di laurea: 11/07/2023'
    }
  },
  {
    period: '2023-Ongoing',
    school: {
      en: 'Polytechnic University of Turin',
      it: 'Politecnico di Torino'
    },
    degree: {
      en: 'Master in Architecture for Sustainability',
      it: 'Master in Architecture in Architecture for Sustainability'
    },
    notes: {
      en: 'Course taught in English',
      it: 'Corso di lingua inglese'
    }
  }
];

const experiences = [
  {
    period: 'Sep22-Dec23',
    title: {
      en: 'Internship at the Architectural Modeling Laboratory - DIDA Labs Unifi',
      it: 'Stage presso il Laboratorio di modellazione architettonica - DIDA Labs Unifi'
    },
    description: {
      en: 'Support for the creation of models and exhibitions by students using mechanical milling, laser cutting, plaster, and cement.',
      it: 'Supporto per la realizzazione di modelli e mostre da parte degli studenti con fresatura meccanica, taglio laser, gesso e cemento.'
    }
  },
  {
    period: 'Mar24-Aug24',
    title: {
      en: 'Research Fellow at ModLab Arch, model laboratory of PoliTO',
      it: 'Borsista del ModLab Arch, laboratorio di maquettes del PoliTO'
    },
    description: {
      en: 'Support and development of models for students and POLITO.',
      it: 'Supporto e sviluppo di maquette per studenti e POLITO.'
    }
  },
  {
    period: 'Mar25-Jul25',
    title: {
      en: 'Architecture internship at Fluidiforme, Turin',
      it: 'Stage di architettura presso Fluidiforme, Torino'
    },
    description: {
      en: 'Architecture and research, focused on questioning and exploring spatial processes through the lens of contemporary issues.',
      it: 'Architettura e ricerca, incentrate sulla messa in discussione e sull’esplorazione dei processi spaziali attraverso la lente delle problematiche contemporanee.'
    }
  },
  {
    period: 'Mar25-Dec25',
    title: {
      en: 'Research Fellow at BBCC - Geomatics Laboratory for Cultural Heritage of PoliTO',
      it: 'Borsista presso il BBCC - Laboratorio di geomatica per i beni culturali del PoliTO'
    },
    description: {
      en: 'The laboratory is equipped with hardware and software for the execution and control of metric surveys of building heritage on site.',
      it: 'Il Laboratorio è dotato di attrezzature hardware e software per l’esecuzione e il controllo di rilievi metrici del patrimonio edilizio sul sito.'
    }
  },

];



const softwareSkills = [
  { name: { en: 'Blender3d', it: 'Blender3d' }, score: 6 },
  { name: { en: 'Autocad2D', it: 'Autocad2D' }, score: 5 },
  { name: { en: 'Rhino3d', it: 'Rhino3d' }, score: 5 },
  { name: { en: 'Affinity', it: 'Affinity' }, score: 5 },
  { name: { en: 'Photoshop', it: 'Photoshop' }, score: 4 },
  { name: { en: 'Indesign', it: 'Indesign' }, score: 5 },
  { name: { en: 'Illustrator', it: 'Illustrator' }, score: 4 },
  { name: { en: 'Agisoft Metashape', it: 'Agisoft Metashape' }, score: 6 },
  { name: { en: 'RealityScan', it: 'RealityScan' }, score: 6 },
  { name: { en: 'Excel', it: 'Excel' }, score: 4 },
];

interface CurriculumProps {
  lang: 'en' | 'it';
}

/**
 * Curriculum Component
 * 
 * Displays the CV/Resume section, including:
 * - Personal Info Header
 * - Education Timeline
 * - Experience Timeline
 * - Software Skills with progress bars
 */
export const Curriculum: React.FC<CurriculumProps> = ({ lang }) => {
  return (
    <section id="curriculum" className={styles.curriculumSection}>
      <div className={styles.container}>
        {/* Header Section: Name, Title, Location, Languages */}
        <div className={styles.header}>
          <h2 className={styles.name}>Lorenzo Quadrelli</h2>
          <div className={styles.subHeader}>{lang === 'en' ? 'MSc Architecture for Sustainability student' : 'Studente MSc Architettura per la Sostenibilità'}</div>
          <div className={styles.location}>{lang === 'en' ? 'Turin, Italy' : 'Torino, Italia'}</div>
          <div className={styles.languages}><strong>{lang === 'en' ? 'Languages:' : 'Lingue:'}</strong> {lang === 'en' ? 'Italian (native), Professional English (IELTS C1, score 7.5), Basic French (DELF A2)' : 'Madrelingua italiano, Inglese professionale (IELTS C1, score 7.5), Francese base (DELF A2)'}</div>
        </div>

        <div className={styles.divider}></div>

        {/* Education Section */}
        <h2 className={styles.sectionTitle}>{lang === 'en' ? 'Education' : 'Istruzione'}</h2>
        <div className={styles.timelineContainer}>
          <div className={styles.timelineLine}></div>
          {education.map((edu, idx) => (
            <div key={idx} className={styles.timelineItem}>
              <div className={styles.timelineDate}>{edu.period}</div>
              <div className={styles.timelineMarker}></div>
              <div className={styles.contentContainer}>
                <div className={styles.itemTitle}>{edu.school[lang]}</div>
                <div className={styles.itemSubtitle}>{edu.degree[lang]}</div>
                {edu.thesis && <div className={styles.itemDetail}>{edu.thesis[lang]}</div>}
                {edu.graduation && <div className={styles.itemDetail}>{edu.graduation[lang]}</div>}
                {edu.notes && <div className={styles.itemDetail}>{edu.notes[lang]}</div>}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.divider}></div>

        {/* Experience Section */}
        <h2 className={styles.sectionTitle}>{lang === 'en' ? 'Experience' : 'Esperienze'}</h2>
        <div className={styles.timelineContainer}>
          <div className={styles.timelineLine}></div>
          {experiences.map((exp, idx) => (
            <div key={idx} className={styles.timelineItem}>
              <div className={styles.timelineDate}>{exp.period}</div>
              <div className={styles.timelineMarker}></div>
              <div className={styles.contentContainer}>
                <div className={styles.itemTitle}>{exp.title[lang]}</div>
                <div className={styles.itemDetail}>{exp.description[lang]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Skills Section */}
      <div className={styles.bottomSection}>
        <h2 className={styles.smallSectionTitle}>{lang === 'en' ? 'Software skills' : 'Competenze software'}</h2>
        <ul className={styles.softwareList}>
          {softwareSkills.map((s, idx) => (
            <li key={idx} className={styles.softwareItem}>
              <span className={styles.softwareName}>{s.name[lang]}</span>
              <div
                className={styles.dotsContainer}
                role="img"
                aria-label={`${s.name[lang]}: ${s.score} / 6`}
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <span
                    key={i}
                    className={`${styles.dot} ${i < s.score ? styles.dotFilled : styles.dotEmpty}`}
                  />
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
